/* Mobile nav drawer — dependency-free replacement for theme.js's
 * jQuery-based theme.MobileNav toggle.
 *
 * Why this exists: theme.js is loaded as type="lazyJs" by a third-party
 * lazy-load app (see snippets/w3_header.liquid), which also installs a
 * global click interceptor on <html> that queues and replays the FIRST
 * click on anything until its own deferred scripts finish loading. That
 * made the hamburger menu feel slow or occasionally do nothing on the
 * first tap. This module loads eagerly (not deferred) and binds its click
 * handler on the CAPTURE phase with stopImmediatePropagation(), so it
 * always runs immediately and never reaches that interceptor — regardless
 * of whether the lazy-load app has finished yet.
 *
 * Drives the exact same CSS classes the theme already styles
 * (mobile-nav--open/--close on the toggle, js-menu--is-open on the
 * container) so no CSS changes are required.
 */
(function () {
  'use strict';

  function init() {
    var toggle = document.querySelector('.js-mobile-nav-toggle');
    var wrapper = document.querySelector('.mobile-nav-wrapper');
    var mobileNav = document.getElementById('MobileNav');
    // .mobile-nav-wrapper is position:absolute but its own ancestor
    // (.site-header) is never itself positioned, so top:0 actually anchors
    // to the page's root coordinate system — not "top of the header". And
    // since the real header stack (shipping bar + header) is
    // position:sticky (viewport-relative), the two don't share a reference
    // frame once the page is scrolled. Measuring + positioning against
    // .cc-top-sticky's live viewport rect (same technique already used to
    // fix the mega-menu dropdown) sidesteps both problems.
    var stickyStack = document.querySelector('.cc-top-sticky');

    if (!toggle || !wrapper || !mobileNav) return;

    var isOpen = false;
    var lastFocused = null;

    function headerHeight() {
      return stickyStack ? Math.round(stickyStack.getBoundingClientRect().bottom) : 0;
    }

    function focusableElements(container) {
      return Array.prototype.slice.call(
        container.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(function (el) {
        return el.offsetParent !== null; // visible only
      });
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        close();
        return;
      }
      if (e.key !== 'Tab') return;

      var focusables = focusableElements(wrapper);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function onOutsideClick(e) {
      // cc-mega-menu.liquid's mobile popups (.cc-cn-section, opened via
      // .cc-mm-trigger for nav groups like "Blog"/"More") render as a
      // separate sibling block outside .mobile-nav-wrapper entirely — not
      // nested inside it. Tapping that popup's own close button/overlay is
      // a legitimate "outside the drawer" click by DOM position, but it
      // shouldn't also close this drawer underneath it — the popup already
      // handles its own dismissal via cc-mega-menu.js.
      if (e.target.closest('.cc-cn-section, .cc-Making-subcategories-section')) return;

      if (!wrapper.contains(e.target) && e.target !== toggle && !toggle.contains(e.target)) {
        close();
      }
    }

    // .mobile-nav-wrapper is display:none until .js-menu--is-open is added
    // (theme.scss.liquid), and its CSS closed position is translateY(-100%)
    // — 100% of the drawer's OWN height, which is much taller than the
    // header. That made it visually fly in from way above the header
    // instead of sliding out from directly beneath it. Instead we compute
    // the drawer's real rendered height and start it at
    // (headerOffset - drawerHeight), so its bottom edge sits flush against
    // the header before opening — it then only ever travels its own
    // height, appearing to emerge from the header itself.
    var CLOSE_TRANSITION_MS = 450; // matches $transition-drawer duration
    var hideTimer = null;

    function closedTransformY(offset) {
      return offset - wrapper.offsetHeight;
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      clearTimeout(hideTimer);

      var offset = headerHeight();

      // Fixed (viewport-relative) instead of the CSS default absolute
      // (page-relative) — matches the sticky header's own reference frame
      // at any scroll position, and a z-index high enough to render above
      // the sticky header stack (which is itself z-index:999) rather than
      // being sandwiched behind/through it.
      wrapper.style.position = 'fixed';
      wrapper.style.zIndex = '1001';

      // Reveal (display:block) and snap it to "just above the header"
      // instantly (transition disabled for this step) — only NOW can we
      // measure its real height, since display:none elements report 0.
      wrapper.classList.add('js-menu--is-open');
      wrapper.style.transition = 'none';
      wrapper.style.transform = 'translateY(' + closedTransformY(offset) + 'px)';

      // Force the browser to commit that starting position as a real
      // frame before re-enabling the transition and animating — otherwise
      // this and the next transform change collapse into one frame and
      // there's nothing to visually transition from.
      // eslint-disable-next-line no-unused-expressions
      wrapper.offsetHeight;
      wrapper.style.transition = '';

      requestAnimationFrame(function () {
        wrapper.style.transform = 'translateY(' + offset + 'px)';
      });

      toggle.classList.add('mobile-nav--close');
      toggle.classList.remove('mobile-nav--open');
      toggle.setAttribute('aria-expanded', 'true');

      document.addEventListener('keydown', onKeydown);
      document.addEventListener('click', onOutsideClick, true);

      var focusables = focusableElements(wrapper);
      if (focusables.length) focusables[0].focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      clearTimeout(hideTimer);

      // Wrapper is still visible/rendered at this point, so offsetHeight
      // reflects its real height — slide back up by exactly that much,
      // same distance it came down, instead of off to -100% of itself.
      wrapper.style.transform = 'translateY(' + closedTransformY(headerHeight()) + 'px)';

      toggle.classList.add('mobile-nav--open');
      toggle.classList.remove('mobile-nav--close');
      toggle.setAttribute('aria-expanded', 'false');

      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('click', onOutsideClick, true);

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      } else {
        toggle.focus();
      }

      // Wait for the close transition to finish before switching back to
      // display:none — removing it earlier would cut the animation short.
      hideTimer = setTimeout(function () {
        if (!isOpen) wrapper.classList.remove('js-menu--is-open');
      }, CLOSE_TRANSITION_MS);
    }

    function toggleDrawer(e) {
      e.preventDefault();
      // stopImmediatePropagation (not just stopPropagation) matters here:
      // once the lazy-load app finishes and theme.js's own jQuery handler
      // attaches to this same button, both listeners would otherwise fire
      // on the same click (this one, then that one) and cancel each other
      // out. Immediate-stop guarantees only this handler ever runs.
      e.stopImmediatePropagation();
      if (isOpen) {
        close();
      } else {
        open();
      }
    }

    // Capture phase + stopImmediatePropagation: fires first and only,
    // ahead of the lazy-load app's bubble-phase click interceptor on
    // <html>, and ahead of any later same-element bubble-phase handler.
    toggle.addEventListener('click', toggleDrawer, { capture: true });

    // Keep the open drawer's offset correct if the header height changes
    // (e.g. free-shipping-bar showing/hiding, viewport resize).
    window.addEventListener('resize', function () {
      if (isOpen) wrapper.style.transform = 'translateY(' + headerHeight() + 'px)';
    });

    // Expose close() for other scripts that need to dismiss the drawer
    // (e.g. search drawer opening) without depending on theme.js's module.
    window.MobileNavDrawer = { open: open, close: close, isOpen: function () { return isOpen; } };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
